import useSWR from "swr"
import { getIntegrations } from "@/api/integration"
import { Button } from "@/components/ui/button.tsx"
import { PortalLoginSection } from "../portal_login_section"

/** `integration.name` of the row that holds the HighRadius login, beside `walmart`. */
export const WALMART_HIGHRADIUS_INTEGRATION = "walmart-highradius"
export const HIGHRADIUS_URL = "https://walmart.highradius.com"
/** Walmart's desk for HighRadius access, as printed on its own bills. */
export const HIGHRADIUS_SUPPORT_EMAIL = "HiRadCS@walmart.com"

/** A supplier the Retail Link bot is mapped to (`WalmartConfig.mapped_vendors`). */
export interface WalmartVendor {
  vendor_number: string
  name?: string | null
}

/** The vendor numbers a HighRadius request should name: the pinned one, else every mapped supplier. */
export function vendors_for_request(data: Record<string, unknown> | null | undefined): WalmartVendor[] {
  const mapped = Array.isArray(data?.mapped_vendors) ? (data.mapped_vendors as WalmartVendor[]) : []
  const pinned = typeof data?.vendor_number === "string" ? data.vendor_number.trim() : ""
  if (!pinned) return mapped
  const named = mapped.find((v: WalmartVendor) => v.vendor_number === pinned)
  return [named ?? { vendor_number: pinned }]
}

/**
 * A `mailto:` to Walmart's HighRadius desk asking for access, with the brand's vendor numbers filled
 * in when the Retail Link sign-in told us them. Sent from the brand's own mail, because the desk
 * sets the login up for the supplier that asks.
 */
export function highradius_access_request_mailto(vendors: WalmartVendor[]): string {
  const company = vendors.find((v: WalmartVendor) => v.name)?.name ?? "[company name]"
  const numbers = vendors.length ? vendors.map((v: WalmartVendor) => v.vendor_number).join(", ") : "[vendor number]"
  const subject = `HighRadius access request - vendor ${numbers}`
  const body = [
    "Hello,",
    "",
    "We would like access to Walmart's HighRadius portal so we can see the bills behind our deductions and their backup.",
    "",
    `Company: ${company}`,
    `Walmart vendor number${vendors.length > 1 ? "s" : ""}: ${numbers}`,
    "",
    "Please set up a login for:",
    "Name: [name]",
    "Email: [email address]",
    "",
    "Thank you,",
  ].join("\n")
  return `mailto:${HIGHRADIUS_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

interface HighRadiusLoginSectionProps {
  /** `integration.username` of the org's `walmart-highradius` row, when it has one. */
  username?: string | null
  onSaved: () => void
  className?: string
}

/**
 * Save the brand's HighRadius login on its `walmart-highradius` integration. Retail Link shows the
 * amount Walmart takes for a bill; HighRadius is where the bill, its backup and its disputes are.
 * The login is checked by the first sync rather than here, because HighRadius allows one session
 * per user and the person typing is usually signed in.
 */
export function HighRadiusLoginSection({ username, onSaved, className }: HighRadiusLoginSectionProps) {
  // The Retail Link sign-in saves the vendor numbers the bot is mapped to; the request names them.
  const { data: walmart_result } = useSWR(["integrations", "walmart"], () => getIntegrations({ name: "walmart" }))
  const walmart_data = walmart_result?.ok ? walmart_result.value.data[0]?.data : undefined
  const vendors = vendors_for_request(walmart_data)

  return (
    <PortalLoginSection
      integration_name={WALMART_HIGHRADIUS_INTEGRATION}
      portal_label="HighRadius"
      username={username}
      username_label="HighRadius username"
      onSaved={onSaved}
      className={className}>
      <p class="text-sm text-gray-500">
        Walmart bills fines, co-op agreements and audit claims through a second portal,{" "}
        <a href={HIGHRADIUS_URL} target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">
          HighRadius
        </a>
        . Retail Link shows the amount coming off a check; HighRadius is the only place that says what each bill was for,
        holds its backup, and takes the dispute. With a login saved we read it every weekday.
      </p>
      <p class="text-xs text-gray-400">
        HighRadius signs a user out when the same login signs in somewhere else, so a login used only for this sync
        works best: your HighRadius administrator can add one under Manage Users.
      </p>
      <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
        <p class="text-sm text-gray-700">
          No HighRadius login yet? Walmart&apos;s HighRadius desk ({HIGHRADIUS_SUPPORT_EMAIL}) sets one up for your
          company.
          {vendors.length > 0 && (
            <>
              {" "}
              We&apos;ve filled in your vendor number{vendors.length > 1 ? "s" : ""} (
              {vendors.map((v: WalmartVendor) => v.vendor_number).join(", ")}).
            </>
          )}
        </p>
        <Button asChild variant="outline" size="sm">
          <a href={highradius_access_request_mailto(vendors)}>
            Request HighRadius access
          </a>
        </Button>
      </div>
    </PortalLoginSection>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("highradius_access_request_mailto", () => {
    it("names the company and every mapped vendor number", () => {
      const url = highradius_access_request_mailto([
        { vendor_number: "56836", name: "Brand Inc" },
        { vendor_number: "123456" },
      ])
      expect(url.startsWith("mailto:HiRadCS@walmart.com?subject=")).toBe(true)
      const body = decodeURIComponent(url.split("&body=")[1])
      expect(body).toContain("Company: Brand Inc")
      expect(body).toContain("Walmart vendor numbers: 56836, 123456")
      expect(decodeURIComponent(url.split("&body=")[0])).toContain("vendor 56836, 123456")
    })

    it("leaves blanks to fill when Walmart has not told us the vendor number", () => {
      const body = decodeURIComponent(highradius_access_request_mailto([]).split("&body=")[1])
      expect(body).toContain("Company: [company name]")
      expect(body).toContain("Walmart vendor number: [vendor number]")
    })
  })

  describe("vendors_for_request", () => {
    it("prefers the pinned vendor number over the rest of the mapping", () => {
      const data = {
        vendor_number: " 56836 ",
        mapped_vendors: [{ vendor_number: "111" }, { vendor_number: "56836", name: "Brand Inc" }],
      }
      expect(vendors_for_request(data)).toEqual([{ vendor_number: "56836", name: "Brand Inc" }])
    })

    it("uses every mapped supplier when none is pinned, and nothing without a connection", () => {
      expect(vendors_for_request({ mapped_vendors: [{ vendor_number: "111" }] })).toEqual([{ vendor_number: "111" }])
      expect(vendors_for_request(undefined)).toEqual([])
    })
  })

  describe("the HighRadius row", () => {
    it("is its own integration beside walmart", () => {
      expect(WALMART_HIGHRADIUS_INTEGRATION).toBe("walmart-highradius")
      expect(HIGHRADIUS_URL).toBe("https://walmart.highradius.com")
    })
  })
}
